import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Tabs, Tab, Form, Button, Alert, Spinner, Image } from 'react-bootstrap';
import { authAPI, usersAPI } from '../../services/apiService';
import { Upload, message } from 'antd';
import { UserOutlined, UploadOutlined, PhoneOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { getTranslatedError, parseApiValidationError } from '../../utils/errorTranslator';
import History from './History';
import Payment from './Payment';
import './Profile.css';

const Profile = ({ user, onUpdate, initialTab, onInitialTabHandled, preselectedTariffType, preselectedTariffId, onPreselectedTariffConsumed }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab || 'info');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    nickname: '',
  });
  const [verificationPhone, setVerificationPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  // Проверяем, есть ли редирект после оплаты, и переключаемся на вкладку оплаты
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentComplete = urlParams.get('paymentComplete');
    
    if (paymentComplete === 'true') {
      setActiveTab('payment');
    }
  }, []);

  // Открытие вкладки по клику "Пополнить" с главной
  useEffect(() => {
    if (initialTab === 'payment') {
      setActiveTab('payment');
      onInitialTabHandled?.();
    }
  }, [initialTab, onInitialTabHandled]);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getProfile();
      // Сервер возвращает { user: {...} }
      const data = response.user || response;
      setProfile(data);
      setFormData({
        firstName: data.firstName || data.first_name || '',
        lastName: data.lastName || data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',
        nickname: data.nickname || '',
      });
      setVerificationPhone(data.phone || '');
    } catch (err) {
      setError('Ошибка при загрузке профиля: ' + getTranslatedError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    const nickname = formData.nickname.trim();
    if (!nickname) {
      setFieldErrors({ nickname: 'Укажите никнейм' });
      setError('Укажите никнейм');
      setSaving(false);
      return;
    }

    try {
      const response = await usersAPI.updateProfile({ ...formData, nickname });
      // Сервер возвращает { message: '...', user: {...} }
      const updated = response.user || response;
      setProfile(updated);
      setSuccess('Профиль успешно обновлен');
      
      // Обновляем пользователя в App
      if (onUpdate) {
        onUpdate(updated);
      }
    } catch (err) {
      const { message: errorMessage, fieldErrors: apiFieldErrors } = parseApiValidationError(
        err,
        'Ошибка при обновлении профиля'
      );
      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors(apiFieldErrors);
      }
      setError('Ошибка при обновлении профиля: ' + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (file) => {
    try {
      setError('');
      const response = await usersAPI.uploadAvatar(file);
      // Сервер возвращает { message: '...', avatarBase64: '...' }
      const avatarBase64 = response.avatarBase64 || response.avatar;
      setProfile(prev => ({ ...prev, avatarBase64, avatar: avatarBase64 }));
      message.success('Аватар успешно загружен');
      
      // Обновляем пользователя в App
      if (onUpdate) {
        onUpdate({ ...profile, avatarBase64, avatar: avatarBase64 });
      }
    } catch (err) {
      message.error('Ошибка при загрузке аватара: ' + getTranslatedError(err));
    }
    return false; // Предотвращаем автоматическую загрузку
  };

  const isPhoneVerified =
    profile?.phoneVerified ??
    profile?.isPhoneVerified ??
    profile?.phone_verified ??
    profile?.is_phone_verified ??
    false;

  const handleVerifyPhone = async () => {
    if (!verificationPhone) {
      setError('Укажите номер телефона для подтверждения.');
      return;
    }
    if (!verificationCode) {
      setError('Введите код подтверждения.');
      return;
    }

    try {
      setVerifying(true);
      setError('');
      setSuccess('');
      const response = await authAPI.verifyPhone(verificationPhone, verificationCode);

      if (response?.accessToken) {
        localStorage.setItem('accessToken', response.accessToken);
      }
      if (response?.refreshToken) {
        localStorage.setItem('refreshToken', response.refreshToken);
      }
      if (response?.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        setProfile(response.user);
        if (onUpdate) {
          onUpdate(response.user);
        }
      } else {
        setProfile(prev => ({ ...(prev || {}), phoneVerified: true }));
      }

      setVerificationCode('');
      setSuccess('Телефон подтвержден');
    } catch (err) {
      setError('Ошибка подтверждения телефона: ' + getTranslatedError(err));
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!verificationPhone) {
      setError('Укажите номер телефона для подтверждения.');
      return;
    }
    try {
      setResending(true);
      setError('');
      setSuccess('');
      await authAPI.resendVerification(verificationPhone);
      setSuccess('Код подтверждения отправлен повторно');
    } catch (err) {
      setError('Ошибка при отправке кода: ' + getTranslatedError(err));
    } finally {
      setResending(false);
    }
  };

  const getAvatarUrl = () => {
    const avatar = profile?.avatarBase64 || profile?.avatar;
    if (avatar) {
      if (avatar.startsWith('http')) {
        return avatar;
      }
      if (avatar.startsWith('data:')) {
        return avatar;
      }
      // Если это относительный путь
      const baseUrl = process.env.NODE_ENV === 'development'
        ? 'http://92.63.106.142:4000'
        : '';
      return `${baseUrl}${avatar}`;
    }
    return null;
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <Row className="justify-content-center">
          <Col lg={10}>
            <Card>
              <Card.Body className="text-center p-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">Загрузка профиля...</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col xl={10} lg={11}>
          <Card className="shadow-sm">
            <Card.Body className="p-3 p-lg-4">
              <Card.Title className="mb-3">Личный кабинет</Card.Title>
              
              <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                <Tab eventKey="info" title="Информация">
                  {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
                  {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
                  
                  <div className="profile-avatar-container mb-3">
                    <div className="mb-2">
                      {getAvatarUrl() ? (
                        <Image
                          src={getAvatarUrl()}
                          roundedCircle
                          className="profile-avatar-img"
                        />
                      ) : (
                        <div className="profile-avatar-placeholder">
                          <UserOutlined style={{ fontSize: '60px', color: '#999' }} />
                        </div>
                      )}
                    </div>
                    <Upload
                      accept="image/*"
                      beforeUpload={handleAvatarUpload}
                      showUploadList={false}
                    >
                      <Button variant="outline-primary" icon={<UploadOutlined />}>
                        Загрузить аватар
                      </Button>
                    </Upload>
                  </div>

                  <Form onSubmit={handleSaveProfile}>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Логин</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={profile?.login || user?.login || ''}
                            disabled
                            readOnly
                          />
                          <Form.Text className="text-muted">
                            Логин нельзя изменить
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Роль</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={profile?.role || user?.role || ''}
                            disabled
                            readOnly
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Имя</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="Введите имя"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Фамилия</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Введите фамилию"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            size="sm"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="user@example.com"
                          />
                          <Form.Text className="text-muted">
                            Добавьте ваш email, чтобы электронные чеки об оплате приходили на эту почту.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Телефон</Form.Label>
                          <Form.Control
                            size="sm"
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+71234567890"
                            disabled
                            readOnly
                          />
                          <Form.Text className="text-muted">
                            Телефон нельзя изменить
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Статус телефона</Form.Label>
                          {formData.phone ? (
                            <div className={isPhoneVerified ? 'text-success' : 'text-warning'}>
                              {isPhoneVerified ? (
                                <>
                                  <CheckCircleOutlined className="me-2" />
                                  Телефон подтвержден
                                </>
                              ) : (
                                <>
                                  <PhoneOutlined className="me-2" />
                                  Телефон не подтвержден
                                </>
                              )}
                            </div>
                          ) : (
                            <Form.Text className="text-muted">
                              Телефон не указан
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>

                      {!isPhoneVerified && formData.phone && (
                        <Col md={12}>
                          <Form.Group>
                            <Form.Label>Подтверждение телефона</Form.Label>
                            <div className="d-flex flex-wrap gap-2">
                              <Form.Control
                                size="sm"
                                type="text"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value)}
                                placeholder="Код из SMS"
                                maxLength={6}
                                style={{ maxWidth: '180px' }}
                              />
                              <Button
                                variant="primary"
                                size="sm"
                                type="button"
                                disabled={verifying}
                                onClick={handleVerifyPhone}
                              >
                                {verifying ? 'Проверка...' : 'Подтвердить'}
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                type="button"
                                disabled={resending}
                                onClick={handleResendCode}
                              >
                                {resending ? 'Отправка...' : 'Отправить код'}
                              </Button>
                            </div>
                          </Form.Group>
                        </Col>
                      )}

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Никнейм *</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            name="nickname"
                            value={formData.nickname}
                            onChange={handleInputChange}
                            placeholder="ivan_user"
                            required
                            isInvalid={!!fieldErrors.nickname}
                          />
                          <Form.Control.Feedback type="invalid">
                            {fieldErrors.nickname || 'Укажите никнейм'}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Баланс (запросов)</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={`${profile?.requestsBalance || profile?.requests_balance || 0}`}
                            disabled
                            readOnly
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="mt-3">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={saving}
                        size="sm"
                      >
                        {saving ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            Сохранение...
                          </>
                        ) : 'Сохранить изменения'}
                      </Button>
                    </div>
                  </Form>
                </Tab>

                <Tab eventKey="history" title="История">
                  <History />
                </Tab>

                <Tab eventKey="payment" title="Купить пакет">
                  <Payment
                    user={profile || user}
                    preselectedTariffType={preselectedTariffType}
                    preselectedTariffId={preselectedTariffId}
                    onPreselectedTariffConsumed={onPreselectedTariffConsumed}
                    onBalanceUpdate={(addedRequests) => {
                      // Обновляем локальный профиль
                      const currentBalance = profile?.requestsBalance || profile?.requests_balance || 0;
                      const newBalance = currentBalance + addedRequests;
                      const updatedProfile = {
                        ...profile,
                        requestsBalance: newBalance,
                        requests_balance: newBalance,
                      };
                      setProfile(updatedProfile);
                      
                      // Уведомляем родителя об обновлении
                      if (onUpdate) {
                        onUpdate(updatedProfile);
                      }
                    }}
                  />
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;

